import { processGqlDataAsArray } from '../use-builders-to-process-gql-data';
import type { HtmlFromDotcms } from './converters-from-dotcms';
import {
  convertDotcmsResponseToRecord,
  convertDotcmsStringNonNullable,
  wrapDotcmsHtml,
} from './converters-from-dotcms';
import type { IBaseObjectData } from './dotcms-base-object';
import type { IDialogData } from './dotcms-dialog';
import { DialogGqlHelper } from './dotcms-dialog';
import type { IDotKeyValueData } from './dotcms-dotkeyvalue';
import { DotKeyValueGqlHelper } from './dotcms-dotkeyvalue';
import { buildGqlFieldsQuery } from './gql-builders';
import type { IGqlHelper } from './gql-helper';

export type ILoginFormDobData = IBaseObjectData & {
  formHeadline: HtmlFromDotcms;
  formDescription: HtmlFromDotcms;
  somethingWrongText: HtmlFromDotcms;
  dobFieldName: HtmlFromDotcms;
  dobFieldError: HtmlFromDotcms;
  organizationFieldName: HtmlFromDotcms;
  organizationFieldPlaceholder: string;
  organizationFieldError: HtmlFromDotcms;
  employeeIdFieldName: HtmlFromDotcms;
  employeeIdFieldPlaceholder: string;
  employeeIdFieldTooltip: HtmlFromDotcms;
  employeeIdFieldTooltipOverride: IDotKeyValueData[];
  employeeIdFieldError: HtmlFromDotcms;
  last4FieldName: HtmlFromDotcms;
  last4FieldPlaceholder: string;
  last4FieldTooltip: HtmlFromDotcms;
  last4FieldError: HtmlFromDotcms;
  securityNote: HtmlFromDotcms;
  submitButtonText: HtmlFromDotcms;
  unableToVerifyPopup?: IDialogData;
  unableToEligibilityPopup?: IDialogData;
  alreadyEnrolledPopup?: IDialogData;
};

export const LoginFormDobGqlHelper: IGqlHelper<ILoginFormDobData> = {
  contentTypeName: 'LoginFormDobEmployeeIdGate',
  gqlFieldsQuery: `
    formHeadline
    formDescription
    somethingWrongText
    dobFieldName
    dobFieldError
    organizationFieldName
    organizationFieldPlaceholder
    organizationFieldError
    employeeIdFieldName
    employeeIdFieldPlaceholder
    employeeIdFieldTooltip
    employeeIdFieldTooltipOverride { ${DotKeyValueGqlHelper.gqlFieldsQuery}}
    employeeIdFieldError
    last4FieldName
    last4FieldPlaceholder
    last4FieldTooltip
    last4FieldError
    securityNote
    submitButtonText
    unableToVerifyPopup { ${buildGqlFieldsQuery(DialogGqlHelper)}}
    unableToEligibilityPopup { ${buildGqlFieldsQuery(DialogGqlHelper)}}
    alreadyEnrolledPopup { ${buildGqlFieldsQuery(DialogGqlHelper)}}
  `,
  processGqlData: (gqlResponse: unknown) => {
    if (!gqlResponse) {
      return undefined;
    }

    const {
      formHeadline,
      formDescription,
      somethingWrongText,
      dobFieldName,
      dobDayFieldName,
      dobFieldError,
      organizationFieldName,
      organizationFieldPlaceholder,
      organizationFieldError,
      employeeIdFieldName,
      employeeIdFieldPlaceholder,
      employeeIdFieldTooltip,
      employeeIdFieldTooltipOverride,
      employeeIdFieldError,
      last4FieldName,
      last4FieldPlaceholder,
      last4FieldTooltip,
      last4FieldError,
      securityNote,
      submitButtonText,
      unableToVerifyPopup,
      unableToEligibilityPopup,
      alreadyEnrolledPopup,
    } = convertDotcmsResponseToRecord(gqlResponse);

    return {
      formHeadline: wrapDotcmsHtml(formHeadline),
      formDescription: wrapDotcmsHtml(formDescription),
      somethingWrongText: wrapDotcmsHtml(somethingWrongText),
      dobFieldName: wrapDotcmsHtml(dobFieldName),
      dobDayFieldName: wrapDotcmsHtml(dobDayFieldName),
      dobFieldError: wrapDotcmsHtml(dobFieldError),
      organizationFieldName: wrapDotcmsHtml(organizationFieldName),
      organizationFieldPlaceholder: convertDotcmsStringNonNullable(
        organizationFieldPlaceholder,
      ),
      organizationFieldError: wrapDotcmsHtml(organizationFieldError),
      employeeIdFieldName: wrapDotcmsHtml(employeeIdFieldName),
      employeeIdFieldPlaceholder: convertDotcmsStringNonNullable(
        employeeIdFieldPlaceholder,
      ),
      employeeIdFieldTooltip: wrapDotcmsHtml(employeeIdFieldTooltip),
      employeeIdFieldTooltipOverride:
        processGqlDataAsArray(
          DotKeyValueGqlHelper,
          // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
          employeeIdFieldTooltipOverride,
        ) ?? [],
      employeeIdFieldError: wrapDotcmsHtml(employeeIdFieldError),
      last4FieldName: wrapDotcmsHtml(last4FieldName),
      last4FieldPlaceholder: convertDotcmsStringNonNullable(
        last4FieldPlaceholder,
      ),
      last4FieldTooltip: wrapDotcmsHtml(last4FieldTooltip),
      last4FieldError: wrapDotcmsHtml(last4FieldError),
      securityNote: wrapDotcmsHtml(securityNote),
      submitButtonText: wrapDotcmsHtml(submitButtonText),
      unableToVerifyPopup: DialogGqlHelper.processGqlData(unableToVerifyPopup),
      unableToEligibilityPopup: DialogGqlHelper.processGqlData(
        unableToEligibilityPopup,
      ),
      alreadyEnrolledPopup:
        DialogGqlHelper.processGqlData(alreadyEnrolledPopup),
    };
  },
};
